import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Col, Row, formatAmountText } from '../../lib/utils';
import { useAmountInputCaret } from '../../lib/amountInputCaret';
import { colors } from '../../lib/colors';
import { Body2Regular, Body3Regular, CaptionLight, Subtitle1Bold } from '../../lib/font';
import { useCreateFacilityProduct, useFacilityProductList } from '@/features/facilityProduct/hooks';
import {
    CreateFacilityProductBody,
    FacilityProductCategory,
    IFacilityProduct,
} from '@/features/facilityProduct/types';
import { CreateSubscriptionBody, PaymentMethod } from '@/features/facilitySubscription/types';
import FacilityProductFormModal from '../FacilityProduct/FacilityProductFormModal';

const PAYMENT_OPTIONS: { code: PaymentMethod; label: string }[] = [
    { code: 'card', label: '카드' },
    { code: 'cash', label: '현금' },
    { code: 'transfer', label: '이체' },
];

const CATEGORY_ORDER: Record<FacilityProductCategory, number> = {
    gym: 0,
    clothing: 1,
    locker: 2,
    etc: 99,
};
const CATEGORY_LABEL: Record<string, string> = {
    gym: '헬스',
    clothing: '운동복',
    locker: '락커',
    etc: '기타',
};

type CategoryFilter = 'all' | 'gym' | 'clothing' | 'locker';
const CATEGORY_TABS: { key: CategoryFilter; label: string; color: string; bg: string }[] = [
    { key: 'all', label: '전체', color: '#1E40AF', bg: '#BFDBFE' },
    { key: 'gym', label: '헬스', color: '#065F46', bg: '#A7F3D0' },
    { key: 'clothing', label: '운동복', color: '#5B21B6', bg: '#DDD6FE' },
    { key: 'locker', label: '락커', color: '#92400E', bg: '#FDE68A' },
];
const CATEGORY_STYLE: Record<string, { color: string; bg: string; border: string }> = {
    gym: { color: '#065F46', bg: '#A7F3D0', border: '#6EE7B7' },
    clothing: { color: '#5B21B6', bg: '#DDD6FE', border: '#C4B5FD' },
    locker: { color: '#92400E', bg: '#FDE68A', border: '#FCD34D' },
};
const getCategoryStyle = (c: string) =>
    CATEGORY_STYLE[c] ?? { color: '#6B7280', bg: '#F3F4F6', border: '#E5E7EB' };

export type BundleItem = {
    productId: string;
    productName: string;
    body: CreateSubscriptionBody;
};

export type BundleRef = {
    isEnabled: () => boolean;
    validate: () => boolean;
    getBodies: () => BundleItem[];
};

type Props = {
    centerId: string;
    membershipStartDate: string; // ISO
    membershipEndDate: string; // ISO
    membershipPaymentDate: string; // ISO (결제일자)
    createAlert?: any;
};

const MembershipBundleFacilitySection = forwardRef<BundleRef, Props>(
    ({ centerId, membershipStartDate, membershipEndDate, membershipPaymentDate, createAlert }, ref) => {
        const [enabled, setEnabled] = useState(false);
        const [selectedIds, setSelectedIds] = useState<string[]>([]);
        // 상품별 결제 금액. 콤마 없는 숫자 문자열만 담는다.
        const [priceMap, setPriceMap] = useState<Record<string, string>>({});
        const amountCaret = useAmountInputCaret({ allowDecimal: true });
        const [lockerNumberMap, setLockerNumberMap] = useState<Record<string, string>>({});
        const [lockNumberMap, setLockNumberMap] = useState<Record<string, string>>({});

        const [startDate, setStartDate] = useState('');
        const [endDate, setEndDate] = useState('');
        const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
        const [memo, setMemo] = useState('');
        const [userEditedDates, setUserEditedDates] = useState(false);
        const [productModalOpen, setProductModalOpen] = useState(false);
        const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');

        const {
            data: productData,
            isLoading: productsLoading,
            isError: productsError,
        } = useFacilityProductList({ centerId, enabled: enabled && !!centerId });
        const createProduct = useCreateFacilityProduct();

        const products = useMemo(() => {
            const raw = (productData?.results ?? []) as IFacilityProduct[];
            return raw
                .filter((p) => p.isActive && p.category !== 'etc')
                .sort((a, b) => {
                    const ca = CATEGORY_ORDER[a.category] ?? 99;
                    const cb = CATEGORY_ORDER[b.category] ?? 99;
                    if (ca !== cb) return ca - cb;
                    return a.name.localeCompare(b.name);
                });
        }, [productData]);

        const categoryCounts = useMemo(() => {
            const base: Record<CategoryFilter, number> = {
                all: products.length,
                gym: 0,
                clothing: 0,
                locker: 0,
            };
            products.forEach((p) => {
                if (p.category === 'gym' || p.category === 'clothing' || p.category === 'locker') {
                    base[p.category]++;
                }
            });
            return base;
        }, [products]);

        const visibleProducts = useMemo(() => {
            if (categoryFilter === 'all') return products;
            return products.filter((p) => p.category === categoryFilter);
        }, [products, categoryFilter]);

        const productById = useMemo(() => {
            const map: Record<string, IFacilityProduct> = {};
            products.forEach((p) => {
                map[p._id] = p;
            });
            return map;
        }, [products]);

        const selectedProducts = useMemo(
            () => selectedIds.map((id) => productById[id]).filter(Boolean) as IFacilityProduct[],
            [selectedIds, productById],
        );

        // 수강권 날짜가 바뀌면 번들 날짜도 동기화 (사용자가 직접 편집하기 전까지)
        useEffect(() => {
            if (userEditedDates) return;
            if (membershipStartDate) setStartDate(dayjs(membershipStartDate).format('YYYY-MM-DD'));
            if (membershipEndDate) setEndDate(dayjs(membershipEndDate).format('YYYY-MM-DD'));
        }, [membershipStartDate, membershipEndDate, userEditedDates]);

        const toggleProduct = (p: IFacilityProduct) => {
            setSelectedIds((prev) => {
                if (prev.includes(p._id)) {
                    return prev.filter((id) => id !== p._id);
                }
                // String(undefined) === 'undefined' → Number → NaN 이 되어 아래 검증을 빠져나간다.
                // 숫자가 아니면 빈칸으로 두어 validate() 가 입력을 요구하게 한다.
                setPriceMap((m) => (m[p._id] != null ? m : { ...m, [p._id]: Number.isFinite(p.price) ? String(p.price) : '' }));
                return [...prev, p._id];
            });
        };

        const removeSelected = (id: string) => {
            setSelectedIds((prev) => prev.filter((x) => x !== id));
        };

        const handleDateEdit = (kind: 'start' | 'end', v: string) => {
            setUserEditedDates(true);
            if (kind === 'start') setStartDate(v);
            else setEndDate(v);
        };

        useImperativeHandle(
            ref,
            () => ({
                isEnabled: () => enabled,
                validate: () => {
                    if (!enabled) return true;
                    if (selectedIds.length === 0) {
                        createAlert?.('필수 입력', '이용권 상품을 하나 이상 선택해주세요', true);
                        return false;
                    }
                    if (!startDate || !endDate) {
                        createAlert?.('필수 입력', '이용권 시작/만료일을 입력해주세요', true);
                        return false;
                    }
                    if (dayjs(endDate).isBefore(dayjs(startDate))) {
                        createAlert?.('입력 오류', '이용권 만료일이 시작일보다 빠를 수 없어요', true);
                        return false;
                    }
                    for (const id of selectedIds) {
                        const p = productById[id];
                        if (!p) continue;
                        const rawPrice = priceMap[id];
                        // Number(raw) 가 NaN 이면 `< 0` 이 false 라 그대로 통과한다. finite 를 먼저 본다.
                        if (rawPrice == null || rawPrice === '' || !Number.isFinite(Number(rawPrice)) || Number(rawPrice) < 0) {
                            createAlert?.('필수 입력', `[${p.name}] 금액을 입력해주세요`, true);
                            return false;
                        }
                        if (p.category === 'locker' && !(lockerNumberMap[id] ?? '').trim()) {
                            createAlert?.('필수 입력', `[${p.name}] 락커 번호를 입력해주세요`, true);
                            return false;
                        }
                    }
                    return true;
                },
                getBodies: () => {
                    if (!enabled || selectedIds.length === 0) return [];
                    return selectedIds
                        .map((id): BundleItem | null => {
                            const p = productById[id];
                            if (!p) return null;
                            /*
                             * 기존 `Number(raw) || 0` 은 파싱 실패를 조용히 0원으로 흘려보냈다.
                             * 실제 관문은 위 validate() 이고 여기는 방어선이다. 숫자가 아니면
                             * 0원짜리 이용권을 만들지 않고 항목 자체를 만들지 않는다.
                             */
                            const rawPrice = priceMap[id];
                            // 빈칸도 막는다. `Number('')` 은 0 이라 "안 적었다"가 0원 결제로 둔갑한다.
                            // 실제 0원 이용권은 관리자가 '0' 을 적으므로 그 경로는 그대로 살아 있다.
                            if (rawPrice == null || rawPrice === '') return null;
                            const parsedPrice = Number(rawPrice);
                            if (!Number.isFinite(parsedPrice) || parsedPrice < 0) return null;
                            const body: CreateSubscriptionBody = {
                                facilityProductId: id,
                                startDate: dayjs(startDate).toISOString(),
                                endDate: dayjs(endDate).toISOString(),
                                price: parsedPrice,
                                paymentDate: membershipPaymentDate || dayjs(startDate).toISOString(),
                                paymentMethod,
                                memo: memo.trim() || undefined,
                            };
                            if (p.category === 'locker') {
                                const locker = (lockerNumberMap[id] ?? '').trim();
                                const lock = (lockNumberMap[id] ?? '').trim();
                                if (locker) body.lockerNumber = locker;
                                if (lock) body.lockNumber = lock;
                            }
                            return { productId: id, productName: p.name, body };
                        })
                        .filter((x): x is BundleItem => x !== null);
                },
            }),
            [
                enabled,
                selectedIds,
                productById,
                priceMap,
                lockerNumberMap,
                lockNumberMap,
                startDate,
                endDate,
                paymentMethod,
                memo,
                membershipPaymentDate,
                createAlert,
            ],
        );

        const handleCreateProduct = async (body: CreateFacilityProductBody & { isActive?: boolean }) => {
            try {
                const res = await createProduct.mutateAsync({ centerId, body });
                setProductModalOpen(false);
                const created = res?.data as IFacilityProduct | null;
                if (created?._id) {
                    setSelectedIds((prev) =>
                        prev.includes(created._id) ? prev : [...prev, created._id],
                    );
                    setPriceMap((m) => ({ ...m, [created._id]: Number.isFinite(created.price) ? String(created.price) : '' }));
                }
                createAlert?.('상품 등록 완료', '이용권 상품이 등록됐어요', false);
            } catch {
                createAlert?.('등록 실패', '이용권 상품 등록에 실패했어요', true);
            }
        };

        const inputClass =
            'border border-solid border-[#D4DBE8] rounded-[8px] px-[12px] py-[10px] text-[14px] w-full';

        return (
            <Col className="mt-[8px] mb-[24px]">
                {/* 활성화 토글 */}
                <Row className="items-center" style={{ gap: 10 }}>
                    <button
                        type="button"
                        onClick={() => setEnabled((v) => !v)}
                        style={{
                            width: 24,
                            height: 24,
                            borderRadius: 4,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            flexShrink: 0,
                            backgroundColor: enabled ? colors.primary : 'white',
                            border: enabled ? 'none' : '2px solid #D4DBE8',
                        }}
                    >
                        {enabled && (
                            <svg width="13" height="10" viewBox="0 0 13 10" fill="none">
                                <path
                                    d="M1.5 5L5 8.5L11.5 1.5"
                                    stroke="white"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        )}
                    </button>
                    <Body2Regular color={colors.LAVEL_4}>시설 이용권도 함께 발급하기</Body2Regular>
                    <CaptionLight color={colors.LAVEL_2}>
                        수강권 기간과 동일한 이용권을 여러 개 한 번에 발급할 수 있어요
                    </CaptionLight>
                </Row>

                {enabled && (
                    <Col
                        className="mt-[14px] p-[20px] rounded-[12px] border border-solid border-[#D4DBE8] bg-[#F8FAFC]"
                        style={{ gap: 16 }}
                    >
                        {/* 상품 선택 (다중 선택) */}
                        <Col style={{ gap: 8 }}>
                            <Row className="items-center justify-between">
                                <Body3Regular>이용권 상품 (다중 선택 가능)</Body3Regular>
                                <button
                                    type="button"
                                    onClick={() => setProductModalOpen(true)}
                                    style={{
                                        fontSize: 12,
                                        color: '#3B82F6',
                                        padding: '4px 10px',
                                        border: '1px solid #BFDBFE',
                                        borderRadius: 6,
                                        backgroundColor: 'white',
                                        cursor: 'pointer',
                                    }}
                                >
                                    + 새 상품 만들기
                                </button>
                            </Row>
                            {productsLoading ? (
                                <div style={{ padding: 12, color: '#9CA3AF', fontSize: 13 }}>
                                    상품 목록 불러오는 중...
                                </div>
                            ) : productsError ? (
                                <div style={{ padding: 12, color: '#B91C1C', fontSize: 13 }}>
                                    상품 목록을 불러오지 못했어요
                                </div>
                            ) : products.length === 0 ? (
                                <div
                                    style={{
                                        padding: 12,
                                        color: '#6B7280',
                                        fontSize: 13,
                                        backgroundColor: 'white',
                                        border: '1px dashed #D1D5DB',
                                        borderRadius: 8,
                                        textAlign: 'center',
                                    }}
                                >
                                    등록된 이용권 상품이 없어요. 우측 상단에서 새로 만들어보세요.
                                </div>
                            ) : (
                                <>
                                    {/* 카테고리 필터 탭 */}
                                    <Row style={{ gap: 6, flexWrap: 'wrap' }}>
                                        {CATEGORY_TABS.map((tab) => {
                                            const count = categoryCounts[tab.key] ?? 0;
                                            const active = categoryFilter === tab.key;
                                            const hasItems = tab.key === 'all' || count > 0;
                                            return (
                                                <button
                                                    key={tab.key}
                                                    type="button"
                                                    onClick={() => hasItems && setCategoryFilter(tab.key)}
                                                    disabled={!hasItems}
                                                    style={{
                                                        padding: '5px 12px',
                                                        borderRadius: 16,
                                                        fontSize: 12,
                                                        fontWeight: active ? 700 : 500,
                                                        cursor: hasItems ? 'pointer' : 'not-allowed',
                                                        border: active
                                                            ? `1.5px solid ${tab.color}`
                                                            : '1px solid #E5E7EB',
                                                        backgroundColor: active ? tab.bg : 'white',
                                                        color: active
                                                            ? tab.color
                                                            : hasItems
                                                              ? '#6B7280'
                                                              : '#D1D5DB',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 4,
                                                        transition: 'all 0.15s',
                                                    }}
                                                >
                                                    {tab.label}
                                                    <span
                                                        style={{
                                                            fontSize: 10,
                                                            padding: '0 5px',
                                                            borderRadius: 8,
                                                            backgroundColor: active
                                                                ? 'rgba(255,255,255,0.55)'
                                                                : '#F3F4F6',
                                                            color: active ? tab.color : '#9CA3AF',
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {count}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </Row>
                                    {visibleProducts.length === 0 ? (
                                        <div
                                            style={{
                                                padding: '16px 12px',
                                                color: '#9CA3AF',
                                                fontSize: 13,
                                                textAlign: 'center',
                                            }}
                                        >
                                            이 카테고리에 등록된 이용권이 없어요
                                        </div>
                                    ) : (
                                <Col style={{ gap: 4, maxHeight: 220, overflowY: 'auto' }}>
                                    {visibleProducts.map((p) => {
                                        const sel = selectedIds.includes(p._id);
                                        return (
                                            <button
                                                key={p._id}
                                                type="button"
                                                onClick={() => toggleProduct(p)}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 10,
                                                    padding: '10px 12px',
                                                    borderRadius: 8,
                                                    cursor: 'pointer',
                                                    textAlign: 'left',
                                                    border: sel ? '1.5px solid #3B82F6' : '1px solid #E5E7EB',
                                                    backgroundColor: sel ? '#EFF6FF' : 'white',
                                                    transition: 'all 0.15s',
                                                }}
                                            >
                                                {/* 체크박스 */}
                                                <div
                                                    style={{
                                                        width: 18,
                                                        height: 18,
                                                        borderRadius: 4,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: sel ? '#3B82F6' : 'white',
                                                        border: sel ? 'none' : '1.5px solid #D1D5DB',
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {sel && (
                                                        <svg width="11" height="8" viewBox="0 0 11 8" fill="none">
                                                            <path
                                                                d="M1 4L4 7L10 1"
                                                                stroke="white"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    )}
                                                </div>
                                                <span
                                                    style={{
                                                        flex: 1,
                                                        fontSize: 13,
                                                        fontWeight: sel ? 600 : 500,
                                                        color: sel ? '#1E40AF' : '#111827',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {p.name}
                                                </span>
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        color: getCategoryStyle(p.category).color,
                                                        padding: '1px 6px',
                                                        borderRadius: 4,
                                                        backgroundColor: getCategoryStyle(p.category).bg,
                                                        fontWeight: 600,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {CATEGORY_LABEL[p.category] ?? p.category} · {p.duration}일
                                                </span>
                                                <span
                                                    style={{
                                                        fontSize: 13,
                                                        fontWeight: 700,
                                                        color: sel ? '#1E40AF' : '#374151',
                                                        minWidth: 80,
                                                        textAlign: 'right',
                                                    }}
                                                >
                                                    {p.price.toLocaleString()}원
                                                </span>
                                            </button>
                                        );
                                    })}
                                </Col>
                                    )}
                                </>
                            )}
                        </Col>

                        {/* 선택된 상품 상세 (금액/락커 배정) */}
                        {selectedProducts.length > 0 && (
                            <Col style={{ gap: 8 }}>
                                <Body3Regular>선택한 상품 ({selectedProducts.length}개)</Body3Regular>
                                <Col style={{ gap: 10 }}>
                                    {selectedProducts.map((p) => {
                                        const isLocker = p.category === 'locker';
                                        const lockerRequired =
                                            isLocker && !(lockerNumberMap[p._id] ?? '').trim();
                                        const catStyle = getCategoryStyle(p.category);
                                        return (
                                            <Col
                                                key={p._id}
                                                className="p-[14px] rounded-[10px] border border-solid"
                                                style={{
                                                    backgroundColor: 'white',
                                                    gap: 10,
                                                    borderColor: catStyle.border,
                                                    borderLeftWidth: 4,
                                                    borderLeftColor: catStyle.color,
                                                }}
                                            >
                                                <Row
                                                    className="items-center justify-between"
                                                    style={{ gap: 8 }}
                                                >
                                                    <Row
                                                        className="items-center"
                                                        style={{ gap: 8, minWidth: 0, flex: 1 }}
                                                    >
                                                        <Subtitle1Bold
                                                            style={{
                                                                fontSize: 13,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {p.name}
                                                        </Subtitle1Bold>
                                                        <span
                                                            style={{
                                                                fontSize: 11,
                                                                color: catStyle.color,
                                                                padding: '2px 8px',
                                                                borderRadius: 4,
                                                                backgroundColor: catStyle.bg,
                                                                fontWeight: 700,
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            {CATEGORY_LABEL[p.category] ?? p.category}
                                                        </span>
                                                    </Row>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeSelected(p._id)}
                                                        style={{
                                                            fontSize: 12,
                                                            color: '#9CA3AF',
                                                            padding: '4px 8px',
                                                            border: '1px solid #E5E7EB',
                                                            borderRadius: 6,
                                                            backgroundColor: 'white',
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        제거
                                                    </button>
                                                </Row>
                                                <Col style={{ gap: 4 }}>
                                                    <label
                                                        style={{
                                                            fontSize: 12,
                                                            fontWeight: 600,
                                                            color: '#374151',
                                                        }}
                                                    >
                                                        결제 금액 (원)
                                                    </label>
                                                    <input
                                                        className={inputClass}
                                                        type="text"
                                                        inputMode="numeric"
                                                        value={formatAmountText(priceMap[p._id] ?? '')}
                                                        onChange={(e) => {
                                                            // 원칙 1 — 패턴에 안 맞으면 값을 아예 바꾸지 않는다.
                                                            const digits = amountCaret.changeAmount(e, priceMap[p._id] ?? '');
                                                            if (digits == null) return;
                                                            setPriceMap((m) => ({
                                                                ...m,
                                                                [p._id]: digits,
                                                            }));
                                                        }}
                                                        // 붙여넣기는 한 번에 값이 확정되므로 정규형만 받는다(CSV 조각 차단).
                                                        onPaste={amountCaret.pasteAmount}
                                                        onDrop={amountCaret.dropAmount}
                                                        placeholder="0"
                                                    />
                                                </Col>
                                                {isLocker && (
                                                    <Col style={{ gap: 10 }}>
                                                        <Col style={{ gap: 4 }}>
                                                            <Row className="items-center" style={{ gap: 4 }}>
                                                                <label
                                                                    style={{
                                                                        fontSize: 12,
                                                                        fontWeight: 600,
                                                                        color: '#374151',
                                                                    }}
                                                                >
                                                                    락커 번호
                                                                </label>
                                                                <span
                                                                    style={{ fontSize: 11, color: '#EF4444' }}
                                                                >
                                                                    *
                                                                </span>
                                                            </Row>
                                                            <input
                                                                className={inputClass}
                                                                placeholder="예: 12"
                                                                value={lockerNumberMap[p._id] ?? ''}
                                                                onChange={(e) =>
                                                                    setLockerNumberMap((m) => ({
                                                                        ...m,
                                                                        [p._id]: e.target.value,
                                                                    }))
                                                                }
                                                                style={{
                                                                    borderColor: lockerRequired
                                                                        ? '#EF4444'
                                                                        : undefined,
                                                                }}
                                                            />
                                                        </Col>
                                                        <Col style={{ gap: 4 }}>
                                                            <label
                                                                style={{
                                                                    fontSize: 12,
                                                                    fontWeight: 600,
                                                                    color: '#374151',
                                                                }}
                                                            >
                                                                자물쇠 비밀번호 (선택)
                                                            </label>
                                                            <input
                                                                className={inputClass}
                                                                placeholder="예: 1234"
                                                                value={lockNumberMap[p._id] ?? ''}
                                                                onChange={(e) =>
                                                                    setLockNumberMap((m) => ({
                                                                        ...m,
                                                                        [p._id]: e.target.value,
                                                                    }))
                                                                }
                                                            />
                                                            {/* 회원 개별값이다. 칸의 고정값과 다른 값이다. */}
                                                            <span style={{ fontSize: 11, color: '#6B7280' }}>
                                                                이 값은 이 회원에게만 적용됩니다. 자리에 고정된 비밀번호는 「락커 관리」에서 설정하세요.
                                                            </span>
                                                        </Col>
                                                    </Col>
                                                )}
                                            </Col>
                                        );
                                    })}
                                </Col>
                            </Col>
                        )}

                        {/* 공통 날짜 */}
                        <Row style={{ gap: 12 }}>
                            <Col style={{ flex: 1, gap: 6 }}>
                                <Body3Regular>시작일</Body3Regular>
                                <input
                                    className={inputClass}
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => handleDateEdit('start', e.target.value)}
                                />
                            </Col>
                            <Col style={{ flex: 1, gap: 6 }}>
                                <Body3Regular>만료일</Body3Regular>
                                <input
                                    className={inputClass}
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => handleDateEdit('end', e.target.value)}
                                />
                            </Col>
                        </Row>
                        {!userEditedDates && (
                            <CaptionLight color={colors.LAVEL_2}>
                                수강권 기간과 자동으로 맞춰져요. 직접 수정할 수도 있어요.
                            </CaptionLight>
                        )}

                        {/* 공통 결제 수단 */}
                        <Col style={{ gap: 6 }}>
                            <Body3Regular>
                                결제 수단{selectedProducts.length >= 2 ? ' (모든 이용권 공통)' : ''}
                            </Body3Regular>
                            <Row style={{ gap: 6 }}>
                                {PAYMENT_OPTIONS.map((opt) => {
                                    const sel = paymentMethod === opt.code;
                                    return (
                                        <button
                                            key={opt.code}
                                            type="button"
                                            onClick={() => setPaymentMethod(opt.code)}
                                            style={{
                                                flex: 1,
                                                padding: '8px 0',
                                                borderRadius: 8,
                                                fontSize: 13,
                                                fontWeight: sel ? 600 : 400,
                                                backgroundColor: sel ? '#3B82F6' : '#fff',
                                                color: sel ? '#fff' : '#4B5563',
                                                border: sel ? '1px solid #3B82F6' : '1px solid #E1E4E6',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </Row>
                        </Col>

                        {/* 공통 메모 */}
                        <Col style={{ gap: 6 }}>
                            <Body3Regular>
                                메모 (선택{selectedProducts.length >= 2 ? ', 모든 이용권 공통' : ''})
                            </Body3Regular>
                            <textarea
                                className={`${inputClass} resize-none`}
                                rows={2}
                                value={memo}
                                onChange={(e) => setMemo(e.target.value)}
                            />
                        </Col>
                    </Col>
                )}

                <FacilityProductFormModal
                    isVisible={productModalOpen}
                    onClose={() => setProductModalOpen(false)}
                    onSubmit={handleCreateProduct}
                    isLoading={createProduct.isLoading}
                />
            </Col>
        );
    },
);

MembershipBundleFacilitySection.displayName = 'MembershipBundleFacilitySection';

export default MembershipBundleFacilitySection;
